import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';
import type { HansAckerFinding, HansAckerFixStep } from './types';
import { InlineText } from './Rich';
import { severityLabel } from './utils';

interface Props {
  steps: HansAckerFixStep[];
  findings: HansAckerFinding[];
  onOpen: (id: string) => void;
}

const stepColor = (step: HansAckerFixStep, index: number) =>
  index === 0 ? '#ef4444' : step.phase === 'IMMEDIATE' ? '#f97316' : '#f59e0b';

export default function FixFirst({ steps, findings, onOpen }: Props) {
  const theme = useTheme();
  return (
    <Box id="hans-acker-fixfirst" sx={{ scrollMarginTop: 72, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2.5 }}>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Fix first</Typography>
        <Typography sx={{ ml: 'auto', fontSize: 12, color: 'text.disabled' }}>Highest-impact actions from the report</Typography>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 1.5 }}>
        {steps.map((step, i) => {
          const color = stepColor(step, i);
          const finding = findings.find((f) => f.id === step.findingId);
          return (
            <Box
              key={step.findingId}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(step.findingId)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(step.findingId); } }}
              sx={{
                p: 2, borderRadius: 2, cursor: 'pointer', border: '1px solid', borderColor: 'divider', borderLeft: `3px solid ${color}`,
                bgcolor: alpha(theme.palette.text.primary, 0.025), transition: 'background-color .15s',
                '&:hover': { bgcolor: alpha(color, 0.07) },
              }}
            >
              <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: 'text.disabled' }}>
                STEP {i + 1} {'\u00b7'} {step.phase}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.5, mb: 0.75 }}>{step.title}</Typography>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: 'text.secondary' }}><InlineText text={step.description} /></Typography>
              <Typography sx={{ mt: 1.25, fontSize: 11, fontWeight: 700, color: 'primary.main' }}>
                {step.findingId}{finding ? ` \u00b7 ${severityLabel(finding.severity)}` : ''} {'\u2192'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
